import { AnimatePresence, motion, useReducedMotion } from "framer-motion";

import { DURATION, EASE_OUT } from "./motionPresets";

/**
 * Opens and closes a block smoothly instead of popping it in and out.
 *
 *   <Collapse isOpen={isOpen}>…the content that folds away…</Collapse>
 *
 * How it works: framer measures the content and animates the wrapper from
 * `height: 0` to `height: auto` while the content fades, so whatever sits
 * below glides instead of jumping.
 *
 * Overflow is hidden only *while* it animates: once open it goes back to
 * visible, so date pickers and dropdowns inside the block (booking
 * confirmation, recurring booking) can still escape it.
 *
 * The content is unmounted while closed (that is what `AnimatePresence` is
 * for), so screen readers and tab order skip it exactly as they did before.
 *
 * `duration` defaults to the quick DURATION.fast; larger booking-flow blocks
 * pass DURATION.base for a calmer open. With prefers-reduced-motion the
 * content simply appears, with no sliding.
 */
const Collapse = ({ isOpen, children, className, duration = DURATION.fast }) => {
  const shouldReduce = useReducedMotion();
  const openCloseTransition = shouldReduce
    ? { duration: 0 }
    : { duration, ease: EASE_OUT };

  return (
    <AnimatePresence initial={false}>
      {isOpen && (
        <motion.div
          key="collapse-content"
          className={className}
          initial={{ height: 0, opacity: 0, overflow: "hidden" }}
          animate={{
            height: "auto",
            opacity: 1,
            transitionEnd: { overflow: "visible" },
          }}
          exit={{ height: 0, opacity: 0, overflow: "hidden" }}
          transition={openCloseTransition}
        >
          {children}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default Collapse;
